/**
 * Input sanitization utilities
 * Provides content cleaning for text typed by or shown to users
 *
 * React escapes every string it renders as a text node, so text shown through
 * JSX must NOT be HTML-escaped first: that double-escapes it, and an apostrophe
 * (O'qituvchi), a slash or an ampersand shows up as &#039;, &#x2F; or &amp;.
 * Only escapeHtml() produces entities, and only for code that builds an HTML
 * string (dangerouslySetInnerHTML, an email body).
 */

/**
 * Escape HTML special characters
 * Converts dangerous characters to their HTML entity equivalents. Use it only
 * where an HTML string is built; never on text that goes into JSX.
 * @param {string} text - Raw text to escape
 * @returns {string} Escaped text safe for HTML rendering
 */
export function escapeHtml(text) {
  if (!text) return '';

  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
    '/': '&#x2F;',
  };

  return String(text).replace(/[&<>"'/]/g, (char) => map[char]);
}

/**
 * Clean user input text: drop control characters (null bytes and the like) and
 * trim. The wording itself is left alone, so a message that mentions
 * "javascript:" or "on click=" reaches the other side as it was written.
 * @param {string} input - User input text
 * @returns {string} Cleaned text
 */
export function sanitizeInput(input) {
  if (!input || typeof input !== 'string') return '';

  // Remove null bytes and other control characters
  const sanitized = Array.from(input)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return (code >= 0x20 && code !== 0x7f) || char === '\n' || char === '\r' || char === '\t';
    })
    .join('');

  return sanitized.trim();
}

/**
 * Clean text for display in React
 * Returns the text as plain characters: React escapes text nodes itself, so
 * escaping here would show entities instead of apostrophes and slashes.
 * @param {string} text - Text to show
 * @returns {string} Cleaned text
 */
export function sanitizeForDisplay(text) {
  return sanitizeInput(text);
}

/**
 * Sanitize message content for display
 * Removes any potential XSS vectors from messages
 * @param {string} content - Message content
 * @returns {string} Sanitized message content
 */
export function sanitizeMessageContent(content) {
  if (!content || typeof content !== 'string') return '';
  return sanitizeForDisplay(content);
}

/**
 * Sanitize thread content (title or body)
 * @param {string} content - Thread content
 * @returns {string} Sanitized content
 */
export function sanitizeThreadContent(content) {
  if (!content || typeof content !== 'string') return '';
  return sanitizeForDisplay(content);
}

/**
 * Validate and clean form input
 * Checks for minimum length, maximum length, and strips control characters
 * @param {string} input - Form input
 * @param {object} options - { minLength, maxLength }
 * @returns {object} { valid: boolean, sanitized: string, error: string|null }
 */
export function validateAndSanitizeInput(input, options = {}) {
  const { minLength = 1, maxLength = 2000 } = options;

  // Check if input is provided
  if (!input || typeof input !== 'string') {
    return {
      valid: false,
      sanitized: '',
      error: 'Input is required'
    };
  }

  // Check minimum length
  if (input.trim().length < minLength) {
    return {
      valid: false,
      sanitized: '',
      error: `Input must be at least ${minLength} character${minLength > 1 ? 's' : ''}`
    };
  }

  // Check maximum length
  if (input.length > maxLength) {
    return {
      valid: false,
      sanitized: '',
      error: `Input must not exceed ${maxLength} characters`
    };
  }

  // Clean the input
  const sanitized = sanitizeInput(input);

  return {
    valid: true,
    sanitized,
    error: null
  };
}

/**
 * Clean user-provided names (for display)
 * @param {string} name - User name
 * @returns {string} Cleaned name, at most 100 characters
 */
export function sanitizeName(name) {
  if (!name || typeof name !== 'string') return 'Anonymous';
  return sanitizeInput(name).substring(0, 100);
}

/**
 * React component helper for user-provided content
 * @param {string} text - Text to render
 * @returns {string} Cleaned text ready for a React text node
 */
export function renderSafeText(text) {
  return sanitizeForDisplay(text);
}
